/* eslint-disable no-restricted-syntax */
// import { LatLng, icon } from 'leaflet';
import moment from 'moment';

import shipOnline from '@/assets/images/map/online-ship.svg';
import shipOffline from '@/assets/images/map/offline-ship.svg';
import markerOnline from '@/assets/images/map/online-marker.svg';
import markerOffline from '@/assets/images/map/offline-marker.svg';

import { generateTimestampsInSeconds } from '@/util/date';

import AssetSvc from '@/services/AssetSvc';
import PulseSvc from '@/services/PulseSvc';
import SummarySvc from '@/services/SummarySvc';
import DeviceSvc from '@/services/DeviceSvc';
// eslint-disable-next-line no-unused-vars
import LandmarkSvc from '@/services/LandmarkSvc';

import TugBoatIcon from '@/assets/images/map/tug-boat-icon.svg';
import LandingCraftTransportIcon from '@/assets/images/map/landing-craft-transport-icon.svg';
import headingOnline from '@/assets/images/map/online-heading.svg';
import headingOffline from '@/assets/images/map/offline-heading.svg';

import {
  getAllVisibilityStatuses,
  saveVisibilityStatus,
  saveAllVisibilityStatuses,
} from '@/services/dbService';

const state = {
  assets: [],
  liveTrackingId: null,
  isLiveTrackingActive: false,
};

const SINGLE_MAIN_ENGINE_MASS_IDS = [95];

function convertDecimalToDMS(decimal, isLatitude) {
  const degrees = Math.floor(Math.abs(decimal));
  const minutesDecimal = (Math.abs(decimal) - degrees) * 60;
  const minutes = Math.floor(minutesDecimal);
  const seconds = Math.round((minutesDecimal - minutes) * 60);

  // eslint-disable-next-line no-nested-ternary
  const direction = decimal >= 0
    ? (isLatitude ? 'N' : 'E')
    : (isLatitude ? 'S' : 'W');

  return `${degrees}° ${minutes}' ${seconds}'' ${direction}`;
}

const processSummarySeriesData = (summary, firstQueryTimestamp, lastQueryTimestamp) => {
  console.log('[processSummarySeriesData] summary:', summary);
  const timestamps = generateTimestampsInSeconds(firstQueryTimestamp, lastQueryTimestamp, 3600);

  const roundedData = (data) => {
    const rounded = {};
    Object.keys(data || {}).forEach((key) => {
      const roundedKey = Math.round(key / 3600) * 3600;
      rounded[roundedKey] = data[key];
    });
    return rounded;
  };

  const roundedGps = roundedData(summary.gps?.data);
  const roundedRpm = roundedData(summary.rpm?.data);
  const roundedFm = roundedData(summary.flowmeter?.data);
  const roundedAe = roundedData(summary.ae?.data);

  return timestamps.map((timestamp) => {
    const gpsData = roundedGps[timestamp] || {};
    const rpmData = roundedRpm[timestamp] || {};
    const fmData = roundedFm[timestamp] || {};
    const aeData = roundedAe[timestamp] || {};

    const latLong = [gpsData?.additional?.LAST_dlgpLatitude_4, gpsData?.additional?.LAST_dlgpLongitude_5];
    const totalCruise = gpsData?.additional?.SUM_dlgpDistance_3;
    const avgSpeed = gpsData.speed;
    const currentAe = Object.values(aeData).find((v) => v.rpm > 0)?.no + 1;
    const portRpm = rpmData.PORT?.rpm;
    const starboardRpm = rpmData.STARBOARD?.rpm;
    const centerRpm = rpmData.CENTER?.rpm;
    const portInFlowrate = fmData.PORT_Flow;
    const starboardInFlowrate = fmData.STARBOARD_Flow;

    return {
      gpsData,
      rpmData,
      fmData,
      aeData,

      dateTime: moment.unix(timestamp).format('DD-MM-YYYY HH:mm:ss'),
      timestamp,
      latLong,
      heading: gpsData.track,
      totalCruise,
      avgSpeed,
      currentAe,
      portRpm,
      starboardRpm,
      centerRpm,
      portInFlowrate,
      starboardInFlowrate,
    };
  });
};

const mapGpsPulseToAssets = async (currAssets, massDevicesMap, gpsPulseDatas, currTime, mode = 'standard') => Promise.all(
  currAssets.map(async (asset) => {
    const newAsset = { ...asset };
    const assetDevices = massDevicesMap.get(asset.massId) || [];
    newAsset.isVisible = true;
    newAsset.nearestLandmark = null;

    // sat mode: Use massSateliteId to match GPS data
    const isSatMode = mode === 'sat';
    let gpsData;

    if (isSatMode) {
      // Match by massSateliteId
      gpsData = gpsPulseDatas.find((d) => d?.deviceId?.toLowerCase() === asset.massSateliteId?.toLowerCase());
    } else {
      // Standard mode: Match by device mapping
      const gpsDevice = assetDevices.find((v) => v.devcType === 'gps');
      if (gpsDevice) {
        gpsData = gpsPulseDatas.find((d) => d?.deviceId?.toLowerCase() === gpsDevice.devcUniqueId.toLowerCase());
      }
    }

    if (gpsData) {
      const diffTime = parseInt(currTime) - parseInt(gpsData.timestamp);
      newAsset.trail = false;
      newAsset.dotTrail = true;
      newAsset.lastGpsData = gpsData;
      newAsset.lastGpsDataPulse = gpsData;
      newAsset.lastCoordinate = `${convertDecimalToDMS(gpsData.latitude, true)}, ${convertDecimalToDMS(gpsData.longitude, false)}`;
      // eslint-disable-next-line no-undef
      newAsset.lastLatLng = L.latLng(gpsData.latitude, gpsData.longitude);
      newAsset.dataStatus = diffTime < 600;
      let assetIcon;
      let iconSize = [40, 56];

      switch (asset?.massMaty?.matyName?.trim()?.toLowerCase()) {
        case 'tug boat':
          assetIcon = TugBoatIcon;
          iconSize = [60, 86];
          break;
        case 'landing craft transport':
          assetIcon = LandingCraftTransportIcon;
          iconSize = [70, 86];
          break;
        default:
          assetIcon = diffTime > 600 ? shipOffline : shipOnline;
      }

      // eslint-disable-next-line no-undef
      newAsset.headingIcon = L.icon({
        iconUrl: diffTime > 600 ? headingOffline : headingOnline,
        iconSize: [120, 146],
        className: 'heading-icon',
      });
      // eslint-disable-next-line no-undef
      newAsset.icon = L.icon({
        iconUrl: assetIcon,
        iconSize,
        popupAnchor: [0, -41],
        tooltipAnchor: [-50, 50],
        className: 'ship-icon',
      });
      // eslint-disable-next-line no-undef
      newAsset.markerIcon = L.icon({
        iconUrl: diffTime > 600 ? markerOffline : markerOnline,
        iconSize: [35, 61],
        className: 'marker-icon',
      });

      try {
        const landmarkResponse = await LandmarkSvc.landmarks.nearest({
          latitude: gpsData.latitude,
          longitude: gpsData.longitude,
        });
        const landmarkData = landmarkResponse?.data?.data;
        if (landmarkData?.lndmName) {
          newAsset.nearestLandmark = {
            name: landmarkData.lndmName,
            distanceMeters: landmarkData.distanceMeters,
          };
        }
      } catch (error) {
        console.warn('Failed to fetch nearest landmark:', error);
      }
    } else {
      console.warn(`No GPS data found for asset: ${asset.massId}`);
    }
    return newAsset;
  }),
);

const mapOtherPulseToAssets = (
  assetsWithGps,
  massDevicesMap,
  aePulseDatas,
  rpmPulseDatas,
  fmPulseDatas,
  temperaturePulseDatas,
  powermeterPulseDatas,
  isSatMode = false,
) => assetsWithGps.map((asset) => {
  const newAsset = { ...asset };
  const assetDevices = massDevicesMap.get(asset.massId) || [];

  // sat mode: Use massSateliteId to match AE data
  if (isSatMode && asset.massSateliteId) {
    const aeData = aePulseDatas.filter((d) => d.deviceId === asset.massSateliteId);
    if (aeData && aeData.length > 0) newAsset.lastAeData = aeData;
  } else {
    // Standard mode: Use device mapping
    const aeDevice = assetDevices.find((v) => v.devcType === 'ae');
    if (aeDevice) {
      const aeData = aePulseDatas.filter((d) => d.deviceId === aeDevice.devcUniqueId);
      if (aeData) newAsset.lastAeData = aeData;
    }
  }

  const rpmDevice = assetDevices.find((v) => v.devcType === 'rpm');
  if (rpmDevice) {
    const rpmData = rpmPulseDatas.filter((d) => d.deviceId === rpmDevice.devcUniqueId);
    if (rpmData) newAsset.lastRpmData = rpmData;
  }

  const fmDeviceIds = assetDevices.filter((v) => v.devcType === 'flowmeter').map((v) => v.devcUniqueId);
  if (fmDeviceIds.length > 0) {
    const fmData = fmPulseDatas.filter((d) => fmDeviceIds.includes(d.deviceId));
    if (fmData) newAsset.lastFmData = fmData;
  }

  const temperatureDeviceIds = assetDevices.filter((v) => v.devcType === 'temperature').map((v) => v.devcUniqueId);
  if (temperatureDeviceIds.length > 0) {
    const temperatureData = temperaturePulseDatas.filter((d) => temperatureDeviceIds.includes(d.deviceId));
    if (temperatureData) newAsset.lastTemperatureData = temperatureData;
  }

  const powermeterDeviceIds = assetDevices.filter((v) => v.devcType === 'powermeter').map((v) => v.devcUniqueId);
  if (powermeterDeviceIds.length > 0) {
    const powermeterData = powermeterPulseDatas.filter((d) => powermeterDeviceIds.includes(d.deviceId));
    if (powermeterData) newAsset.lastPowermeterData = powermeterData;
  }

  return newAsset;
});

async function getDeviceStatus(devices) {
  if (!devices || devices.length === 0) {
    return {
      hasExhaustTemperature: false,
      flowmeterType: 'none',
      flowmeterCount: 0,
      flowmeterPositions: [],
      flowmeterPositionCounts: {
        PORT: 0,
        STARBOARD: 0,
        CENTER: 0,
      },
      hasPowermeter: false,
      powermeterCount: 0,
      aeCount: 0,
      rpmTypes: [],
      rpmCount: 0,
    };
  }

  // Check for exhaust temperature sensor
  const hasExhaustTemperature = devices.some(
    (d) => d.devcType === 'temperature' && d.devcLabel === 'EXHAUST_TEMPERATURE',
  );

  // Analyze flowmeters
  const flowmeters = devices.filter((d) => d.devcType === 'flowmeter');
  const flowmeterCount = flowmeters.length;

  let flowmeterType = 'none';
  const flowmeterPositionCounts = {
    PORT: 0,
    STARBOARD: 0,
    CENTER: 0,
  };

  if (flowmeterCount > 0) {
    const hasIn = flowmeters.some((d) => d.devcLabel.includes('_IN'));
    const hasOut = flowmeters.some((d) => d.devcLabel.includes('_OUT'));

    if (hasIn && hasOut) {
      flowmeterType = 'in-out';
    } else if (hasIn) {
      flowmeterType = 'in-only';
    }
  }

  // Get flowmeter positions and count them
  flowmeters.forEach((fm) => {
    if (fm.devcLabel.startsWith('PORT')) {
      flowmeterPositionCounts.PORT += 1;
    } else if (fm.devcLabel.startsWith('STARBOARD')) {
      flowmeterPositionCounts.STARBOARD += 1;
    } else if (fm.devcLabel.startsWith('CENTER')) {
      flowmeterPositionCounts.CENTER += 1;
    }
  });

  const flowmeterPositions = Object.keys(flowmeterPositionCounts).filter(
    (pos) => flowmeterPositionCounts[pos] > 0,
  );

  // Analyze powermeters
  const powermeters = devices.filter((d) => d.devcType === 'powermeter');
  const powermeterCount = powermeters.length;
  const hasPowermeter = powermeterCount > 0;

  // --- AE count logic ---
  const aeDevice = devices.find((d) => d.devcType === 'ae');
  let aeCount = 0;

  if (aeDevice) {
    try {
      const pulseData = await PulseSvc.getPulse([aeDevice.devcUniqueId], 'ae');
      if (pulseData && pulseData.length > 0) {
        const maxNo = pulseData.reduce((max, item) => (item.no > max ? item.no : max), -1);
        if (maxNo > -1) {
          aeCount = maxNo + 1;
        }
      }
    } catch (error) {
      console.error(`Failed to fetch AE pulse for ${aeDevice.devcUniqueId}`, error);
    }
  }

  // --- RPM logic ---
  const rpmDevice = devices.find((d) => d.devcType === 'rpm');
  let rpmTypes = [];
  let rpmTypesCount = 0;

  if (rpmDevice) {
    try {
      const pulseData = await PulseSvc.getPulse([rpmDevice.devcUniqueId], 'rpm');
      const singleEngineIds = [6, 97];
      // console.log('rpmDevice found in singleEngineIds', rpmDevice);
      if (singleEngineIds.includes(Number(rpmDevice.devcMassId))) {
        rpmTypes = ['CENTER'];
        rpmTypesCount = 1;
      } else {
        // eslint-disable-next-line no-lonely-if
        if (pulseData && pulseData.length > 0) {
          const uniqueRpmTypes = new Set(pulseData.map((item) => item.type));
          rpmTypes = Array.from(uniqueRpmTypes);
          rpmTypesCount = rpmTypes.length;
        }
      }
    } catch (error) {
      console.error(`Failed to fetch RPM pulse for ${rpmDevice.devcUniqueId}`, error);
    }
  }

  return {
    hasExhaustTemperature,
    flowmeterType,
    flowmeterCount,
    flowmeterPositions,
    flowmeterPositionCounts,
    hasPowermeter,
    powermeterCount,
    aeCount,
    rpmTypes,
    rpmTypesCount,
  };
}

const mutations = {
  UPDATE_ASSET_STATUS(state, { id, status }) {
    const asset = state.assets.find((asset) => asset.id === id);
    if (asset) {
      asset.status = status;
    }
  },
  UPDATE_ASSET_VISIBILITY(state, { massId, isVisible }) {
    const asset = state.assets.find((asset) => asset.massId === massId);
    if (asset) {
      asset.isVisible = isVisible;
    }
  },
  UPDATE_ASSET_TRAIL(state, { massId, trail }) {
    const asset = state.assets.find((asset) => asset.massId === massId);
    if (asset) {
      asset.trail = trail;
    }
  },
  SET_ASSETS(state, assets) {
    state.assets = assets;
  },
  SET_LIVE_TRACKING_ID(state, id) {
    state.liveTrackingId = id;
  },
  SET_LIVE_TRACKING_ACTIVE(state, status) {
    state.isLiveTrackingActive = status;
  },
};

const actions = {
  async getAssetsData({ commit }) {
    const res = await AssetSvc.getAssets();
    const assetsData = res?.data?.data ?? [];

    const processedAssets = assetsData.map((asset) => {
      const newAsset = { ...asset };
      if (newAsset.massDevice) {
        newAsset.devices = newAsset.massDevice;
        delete newAsset.massDevice;
      } else {
        newAsset.devices = [];
      }
      return newAsset;
    });

    commit('SET_ASSETS', processedAssets);
    return processedAssets;
  },

  async getAssetsDevices({ commit, state }) {
    const currAssets = Object.values(state.assets);

    const finalAssetsPromises = currAssets.map(async (asset) => {
      let deviceData = asset.devices || [];

      // If devices are missing, fetch them ONCE.
      if (deviceData.length === 0) {
        try {
          const param = { findField: 'devcMassId', findValue: asset.massId, max: 100 };
          const response = await DeviceSvc.getDevicesByAssetId(param);
          deviceData = response?.data?.data || [];
        } catch (error) {
          console.error(`Failed to fetch devices for asset ${asset.massId}`, error);
          deviceData = []; // Ensure it's an array on failure
        }
      }

      // Now, with the final deviceData (either from master or fetched), calculate status.
      const deviceStatus = await getDeviceStatus(deviceData);

      return {
        ...asset,
        devices: deviceData, // Update with fetched data if it was missing
        deviceStatus,
      };
    });

    const finalAssets = await Promise.all(finalAssetsPromises);

    commit('SET_ASSETS', finalAssets);
    // console.log('[asset.js] Devices processed and status calculated:', finalAssets);
    return finalAssets;
  },

  stopLiveTracking({ commit, state }) {
    if (state.liveTrackingId) {
      clearTimeout(state.liveTrackingId);
      commit('SET_LIVE_TRACKING_ID', null);
    }
    commit('SET_LIVE_TRACKING_ACTIVE', false);
    console.log('[stopLiveTracking] Live tracking stopped manually.');
  },

  async getLastData({ commit, state, dispatch }, mode = 'standard') {
    dispatch('stopLiveTracking');
    if (!state.isLiveTrackingActive) {
      commit('SET_LIVE_TRACKING_ACTIVE', true);
    }

    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve) => {
      if (!state.isLiveTrackingActive) {
        resolve();
        return;
      }

      const currTime = Math.floor(Date.now() / 1000);
      const currAssets = Object.values(state.assets);
      console.log(`[getLastData] Fetching last data for ${currAssets.length} assets in mode: ${mode} at ${new Date(currTime * 1000).toLocaleTimeString()}`);
      // console.log('[getLastData] Current assets:', JSON.stringify(currAssets, null, 2));

      const isSatMode = mode === 'sat';

      // --- sat Mode: Only fetch GPS and RPM using massSateliteId ---
      if (isSatMode) {
        // Get satellite IDs from assets
        const satelliteGpsIds = currAssets
          .filter((asset) => asset.massSateliteId)
          .map((asset) => asset.massSateliteId);

        const satelliteRpmIds = currAssets
          .filter((asset) => asset.massSateliteId)
          .map((asset) => asset.massSateliteId);

        // Fetch GPS data
        const gpsPulseDatas = satelliteGpsIds.length > 0
          ? await PulseSvc.getPulse(satelliteGpsIds, 'gps', 'sat')
          : [];

        // Fetch RPM data
        const rpmPulseDatas = satelliteRpmIds.length > 0
          ? await PulseSvc.getPulse(satelliteRpmIds, 'rpm', 'sat')
          : [];

        // Fetch AE data
        const aePulseDatas = satelliteRpmIds.length > 0
          ? await PulseSvc.getPulse(satelliteRpmIds, 'ae', 'sat')
          : [];

        // Map GPS data to assets (pass 'sat' mode)
        const assetsWithGps = await mapGpsPulseToAssets(currAssets, new Map(), gpsPulseDatas, currTime, 'sat');

        // Map RPM and AE data to assets (pass isSatMode = true)
        const finalMappedAssets = mapOtherPulseToAssets(
          assetsWithGps,
          new Map(),
          rpmPulseDatas,
          aePulseDatas,
          [], // fmPulseDatas
          [], // temperaturePulseDatas
          [], // powermeterPulseDatas
          true, // isSatMode
        );

        commit('SET_ASSETS', finalMappedAssets);

        // DISABLED: Live tracking recursive call (temporary)
        // if (state.isLiveTrackingActive) {
        //   const now = new Date();
        //   const nextTarget = new Date(now);
        //   nextTarget.setSeconds(59);
        //   nextTarget.setMilliseconds(0);
        //   if (now.getTime() >= nextTarget.getTime()) {
        //     nextTarget.setMinutes(nextTarget.getMinutes() + 1);
        //   }

        //   const delayMs = nextTarget.getTime() - now.getTime();

        //   const timeoutId = setTimeout(() => {
        //     dispatch('getLastData', mode);
        //   }, delayMs);

        //   commit('SET_LIVE_TRACKING_ID', timeoutId);
        //   resolve();
        // } else {
        //   console.log('[getLastData] Live tracking stopped, recursion ended.');
        //   resolve();
        // }
        resolve();
        return;
      }

      // --- Standard Mode: Full device mapping and all data types ---
      const massDevicesMap = new Map();
      currAssets.forEach((asset) => {
        massDevicesMap.set(asset.massId, asset.devices || []);
      });

      // --- Stage 1: GPS ---
      const gpsIds = currAssets.flatMap((asset) => (asset.devices || []).filter((d) => d.devcType === 'gps').map((d) => d.devcUniqueId));
      const gpsPulseDatas = gpsIds.length > 0 ? await PulseSvc.getPulse(gpsIds, 'gps', mode) : [];

      const assetsWithGps = await mapGpsPulseToAssets(currAssets, massDevicesMap, gpsPulseDatas, currTime, mode);

      commit('SET_ASSETS', assetsWithGps);

      if (!state.isLiveTrackingActive) return;

      // --- Stage 2: Other Devices ---
      const otherDevicesByType = new Map();
      const excludedPulseTypes = ['gps', 'power'];
      currAssets.forEach((asset) => {
        (asset.devices || []).forEach((device) => {
          if (!excludedPulseTypes.includes(device.devcType)) {
            if (!otherDevicesByType.has(device.devcType)) {
              otherDevicesByType.set(device.devcType, []);
            }
            otherDevicesByType.get(device.devcType).push(device.devcUniqueId);
          }
        });
      });

      const pulsePromises = [];
      const types = [];
      for (const [type, ids] of otherDevicesByType.entries()) {
        const uniqueIds = [...new Set(ids)];
        if (uniqueIds.length > 0) {
          types.push(type);
          pulsePromises.push(PulseSvc.getPulse(uniqueIds, type));
        }
      }

      const pulseResults = await Promise.all(pulsePromises);

      const pulseDataMap = new Map();
      pulseResults.forEach((result, index) => {
        pulseDataMap.set(types[index], result);
      });

      const aePulseDatas = pulseDataMap.get('ae') || [];
      const rpmPulseDatas = pulseDataMap.get('rpm') || [];
      const fmPulseDatas = pulseDataMap.get('flowmeter') || [];
      const temperaturePulseDatas = pulseDataMap.get('temperature') || [];
      const powermeterPulseDatas = pulseDataMap.get('powermeter') || [];

      const finalMappedAssets = mapOtherPulseToAssets(
        assetsWithGps,
        massDevicesMap,
        aePulseDatas,
        rpmPulseDatas,
        fmPulseDatas,
        temperaturePulseDatas,
        powermeterPulseDatas,
      );

      commit('SET_ASSETS', finalMappedAssets);

      // DISABLED: Live tracking recursive call (temporary)
      // if (state.isLiveTrackingActive) {
      //   const now = new Date();
      //   const nextTarget = new Date(now);
      //   nextTarget.setSeconds(59);
      //   nextTarget.setMilliseconds(0);
      //   if (now.getTime() >= nextTarget.getTime()) {
      //     nextTarget.setMinutes(nextTarget.getMinutes() + 1);
      //   }

      //   const delayMs = nextTarget.getTime() - now.getTime();

      //   const timeoutId = setTimeout(() => {
      //     dispatch('getLastData', mode);
      //   }, delayMs);

      //   commit('SET_LIVE_TRACKING_ID', timeoutId);
      //   resolve();
      // } else {
      //   console.log('[getLastData] Live tracking stopped, recursion ended.');
      //   resolve();
      // }
      resolve();
    });
  },

  async fetchSummary({ commit, state }, {
    start, end, interval, massId,
  }) {
    console.log('[fetchSummary] Called with:', {
      start: moment(start).format('YYYY-MM-DD HH:mm:ss'),
      end: moment(end).format('YYYY-MM-DD HH:mm:ss'),
      interval,
      massId,
      massIdType: typeof massId,
    });

    console.log('[fetchSummary] State assets:', {
      assetsCount: Object.values(state.assets).length,
      assetsType: state.assets.constructor.name,
      assetsIds: Object.values(state.assets).map((a) => a.massId),
    });

    const assets = Object.values(state.assets);
    const firstQueryTimestamp = moment(start).unix();
    const lastQueryTimestamp = moment(end).unix();

    console.log('[fetchSummary] Timestamps:', {
      firstQueryTimestamp,
      lastQueryTimestamp,
      durationSeconds: lastQueryTimestamp - firstQueryTimestamp,
    });

    const currentAsset = assets.find((asset) => {
      const match = String(asset.massId) === String(massId);
      console.log('[fetchSummary] Checking asset:', {
        assetMassId: asset.massId,
        assetMassName: asset.massName,
        massId,
        match,
      });
      return match;
    });

    if (!currentAsset) {
      console.error(`[fetchSummary] Asset with massId ${massId} not found.`);
      console.log('[fetchSummary] Available assets:', JSON.stringify(assets.map((a) => ({ massId: a.massId, massName: a.massName }))));
      return assets;
    }

    console.log('[fetchSummary] Asset found:', currentAsset.massName);

    let existingDevcTypes = [];
    if (currentAsset.devices && currentAsset.devices.length > 0) {
      const uniqueTypes = new Set(currentAsset.devices.map((d) => d.devcType));
      existingDevcTypes = Array.from(uniqueTypes);
    }

    existingDevcTypes = existingDevcTypes.filter((type) => type !== 'gateway' && type !== 'limitswitch' && type !== 'power');

    console.log('[fetchSummary] Device types:', existingDevcTypes);

    if (existingDevcTypes.length === 0) {
      console.warn(`[fetchSummary] No devices found on asset ${massId}. Summary fetch skipped.`);
      return assets;
    }

    const summaryPromises = existingDevcTypes.map((type) => SummarySvc.getSummary({
      start: firstQueryTimestamp,
      end: lastQueryTimestamp,
      aggregatedUnit: interval.toUpperCase(),
      devcType: [type],
      devcMassId: massId,
      showDetails: true,
    }));

    console.log('[fetchSummary] Making API calls for device types:', existingDevcTypes);
    const summaryResponses = await Promise.all(summaryPromises);
    console.log('[fetchSummary] API responses received:', summaryResponses.length);

    const summaryData = summaryResponses.reduce((acc, response) => {
      if (response?.data?.data) {
        Object.assign(acc, response.data.data);
      }
      return acc;
    }, {});

    const mappedAssets = assets.map((asset) => {
      if (String(asset.massId) === String(massId)) {
        const mappedCoordinatesData = processSummarySeriesData(summaryData, firstQueryTimestamp, lastQueryTimestamp);

        const updatedAsset = {
          ...asset,
          summary: summaryData,
          coordinates: mappedCoordinatesData.map((coordinate) => coordinate.latLong),
          coordinatesData: mappedCoordinatesData,
        };

        const lastData = mappedCoordinatesData[mappedCoordinatesData.length - 1];
        if (lastData?.gpsData?.additional?.LAST_dlgpLatitude_4 && lastData?.gpsData?.additional?.LAST_dlgpLongitude_5) {
          updatedAsset.lastGpsData = lastData.gpsData;
          // eslint-disable-next-line no-undef
          updatedAsset.lastLatLng = L.latLng(lastData.gpsData?.additional?.LAST_dlgpLatitude_4, lastData.gpsData?.additional?.LAST_dlgpLongitude_5);
        }
        return updatedAsset;
      }
      return asset;
    });

    commit('SET_ASSETS', mappedAssets);
    return mappedAssets;
  },

  async fetchAllLastSummary({ commit, state }) {
    const assets = Object.values(state.assets);
    let mappedAssets = [];

    const assetPromises = assets.map(async (asset) => {
      console.log('[fetchAllLastSummary] debug fetch summary for', asset.massId);
      console.log('[fetchAllLastSummary] last gps', asset.lastGpsData);

      const lastTimestamp = asset.lastGpsData?.timestamp;
      const firstQueryTimestamp = moment((Math.floor(lastTimestamp / 3600) * 3600) * 1000).subtract(1, 'day').unix();
      const lastQueryTimestamp = moment(lastTimestamp * 1000).unix();

      const summaryResponse = await SummarySvc.getSummary({
        start: firstQueryTimestamp,
        end: lastQueryTimestamp,
        aggregatedUnit: 'HOUR',
        devcType: ['gps', 'rpm', 'flowmeter', 'ae'],
        devcMassId: asset.massId,
        showDetails: true,
      });

      const mappedCoordinatesData = processSummarySeriesData(summaryResponse.data.data, firstQueryTimestamp, lastQueryTimestamp);

      asset.coordinates = mappedCoordinatesData.map((coordinate) => coordinate.latLong);
      mappedCoordinatesData.pop();
      asset.coordinatesData = mappedCoordinatesData;
      asset.summary = summaryResponse.data.data;
      return asset;
    });

    mappedAssets = await Promise.all(assetPromises);

    commit('SET_ASSETS', mappedAssets);
    return mappedAssets;
  },

  updateAssetStatus({ commit }, payload) {
    commit('UPDATE_ASSET_STATUS', payload);
  },

  async getAssetsFromAPI({ dispatch, state }) {
    dispatch('stopLiveTracking');

    await dispatch('getAssetsData');
    await dispatch('getAssetsDevices');
    await dispatch('getLastData');

    const initialAssets = state.assets.filter((asset) => asset.lastGpsData);
    return initialAssets;
  },

  async fetchAndReconcileAssets({ dispatch, commit }) {
    const [apiAssets, visibilityMap] = await Promise.all([
      dispatch('getAssetsFromAPI'),
      getAllVisibilityStatuses(),
    ]);

    const reconciledAssets = apiAssets.map((asset) => {
      if (visibilityMap.has(asset.massId)) {
        asset.isVisible = visibilityMap.get(asset.massId);
      } else {
        asset.isVisible = true;
      }
      return asset;
    });

    commit('SET_ASSETS', reconciledAssets);

    const allStatuses = reconciledAssets.map(({ massId, isVisible }) => ({ massId, isVisible }));
    await saveAllVisibilityStatuses(allStatuses);

    return reconciledAssets;
  },

  async updateAssetVisibility({ commit }, payload) {
    commit('UPDATE_ASSET_VISIBILITY', payload);
    await saveVisibilityStatus(payload.massId, payload.isVisible);
  },

  async setAllAssetsVisibility({ commit, state }, isVisible) {
    commit('SET_ALL_ASSETS_VISIBILITY', isVisible);
    const allStatuses = state.assets.map((asset) => ({
      massId: asset.massId,
      isVisible,
    }));
    await saveAllVisibilityStatuses(allStatuses);
  },

  // eslint-disable-next-line no-unused-vars
  updateAssetTrail({ commit }, { massId, trail }) {
    const assets = Object.values(state.assets);

    assets.map((asset) => {
      if (asset.massId === massId) {
        asset.trail = trail;
      }
      return asset;
    });

    commit('SET_ASSETS', assets);
  },

  updateAssetDotTrail({ commit }, { massId, dotTrail }) {
    const assets = Object.values(state.assets);

    assets.map((asset) => {
      if (asset.massId === massId) {
        asset.dotTrail = dotTrail;
      }
      return asset;
    });

    commit('SET_ASSETS', assets);
  },

  resetAssetsData({ dispatch, commit }) {
    dispatch('stopLiveTracking');
    commit('SET_ASSETS', []);
    return [];
  },
};

const getters = {
  assets: (state) => state.assets,
  isSingleMainEngine: () => (massId) => SINGLE_MAIN_ENGINE_MASS_IDS.includes(Number(massId)),
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
